import {
  Button,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Spinner,
} from "@getgreenline/blaze-ui";
import type { UseQueryResult } from "@tanstack/react-query";
import { useAnalyticsDashboardEmbed } from "@/hooks/use-analytics-dashboard-embed";
import type { AnalyticsEmbedUrlResponse } from "@/services/analytics.service";

interface DashboardEmbedPageProps {
  description: string;
  embedInfoQuery: UseQueryResult<AnalyticsEmbedUrlResponse, Error>;
  theme: "dark" | "light";
  title: string;
}

export function DashboardEmbedPage({
  description,
  embedInfoQuery,
  theme,
  title,
}: DashboardEmbedPageProps) {
  const {
    data: embedInfo,
    isPending,
    isError,
    error,
    refetch,
    isFetching,
  } = embedInfoQuery;
  const { containerRef, embedError, isDashboardLoaded, reset } =
    useAnalyticsDashboardEmbed({
      embedUrl: embedInfo?.url,
      lightThemeArn: embedInfo?.lightThemeArn,
      darkThemeArn: embedInfo?.darkThemeArn,
      disabled: isError,
      theme,
    });

  const errorMessage = embedError
    ? embedError
    : error instanceof Error
      ? error.message
      : "Unable to load the dashboard right now.";
  const hasError = isError || embedError !== null;

  return (
    <main className="tw:@container tw:flex tw:min-w-0 tw:flex-col tw:px-6 tw:py-8 sm:tw:px-8">
      <CardHeader className="tw:mb-3 tw:px-0 tw:pt-0 tw:relative tw:flex tw:flex-col tw:gap-4 tw:pr-0 sm:tw:pr-36">
        <div className="tw:space-y-1">
          <CardTitle>
            <h1 className="tw:text-2xl tw:font-semibold">{title}</h1>
          </CardTitle>
          <CardDescription className="tw:max-w-2xl">
            {description}
          </CardDescription>
        </div>
        <CardAction className="tw:absolute tw:right-0 tw:top-0">
          <Button
            className="tw:cursor-pointer"
            disabled={isFetching}
            onClick={() => {
              reset();
              void refetch();
            }}
            variant="outline"
          >
            Refresh
          </Button>
        </CardAction>
      </CardHeader>

      {isPending ? (
        <Card className="tw:flex-1 tw:border-border/70 tw:bg-card/95">
          <CardContent className="tw:flex tw:min-h-120 tw:items-center tw:justify-center tw:p-6">
            <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center">
              <Spinner className="tw:h-8 tw:w-8 tw:text-muted-foreground" />
              <p className="tw:text-sm tw:text-muted-foreground">
                Loading dashboard...
              </p>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {hasError ? (
        <Card className="tw:flex-1 tw:border-border/70 tw:bg-card/95">
          <CardContent className="tw:flex tw:min-h-120 tw:flex-col tw:items-center tw:justify-center tw:gap-4 tw:p-6 tw:text-center">
            <div className="tw:space-y-2">
              <h2 className="tw:text-lg tw:font-semibold">
                Dashboard unavailable
              </h2>
              <p className="tw:max-w-md tw:text-sm tw:text-muted-foreground">
                {errorMessage}
              </p>
            </div>
            <Button
              className="tw:cursor-pointer"
              onClick={() => void refetch()}
            >
              Try again
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {embedInfo && !hasError ? (
        <Card className="tw:flex-1 tw:overflow-hidden tw:border-border/70 tw:bg-card/95">
          <CardContent className="tw:relative tw:p-0">
            {!isDashboardLoaded ? (
              <div className="tw:absolute tw:inset-0 tw:z-10 tw:flex tw:items-center tw:justify-center tw:bg-background/80 tw:backdrop-blur-sm">
                <div className="tw:flex tw:flex-col tw:items-center tw:gap-3 tw:text-center">
                  <Spinner className="tw:h-8 tw:w-8 tw:text-muted-foreground" />
                  <p className="tw:text-sm tw:text-muted-foreground">
                    Rendering dashboard...
                  </p>
                </div>
              </div>
            ) : null}
            <div
              ref={containerRef}
              className="tw:block tw:h-[calc(100vh-14rem)] tw:min-h-180 tw:w-full tw:bg-background"
            />
          </CardContent>
        </Card>
      ) : null}
    </main>
  );
}
